import { useState, useEffect } from "react";
import { Loader2, Plus, Repeat, Trash2, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, EmptyState, StatCard } from "@/components/admin/ui";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { formatGs, formatNumber, formatDate } from "@/lib/format";

const SUB_STATUS = {
  activa: "bg-emerald-100 text-emerald-700",
  aguardando_pago: "bg-amber-100 text-amber-700",
  pausada: "bg-slate-100 text-slate-600",
  cancelada: "bg-rose-100 text-rose-700",
};
const SUB_STATUSES = ["activa", "aguardando_pago", "pausada", "cancelada"];

const Subscriptions = () => {
  const [plans, setPlans] = useState([]);
  const [subs, setSubs] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", service_id: "", quantity: 1000, frequency: "mensual", price: 50000, discount: 0, active: true });

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/admin/plans"), api.get("/admin/subscriptions"), api.get("/admin/services")])
      .then(([p, s, sv]) => { setPlans(p.data); setSubs(s.data); setServices(sv.data); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const savePlan = async () => {
    if (!form.name || !form.service_id) return toast.error("Completá nombre y servicio");
    try { await api.post("/admin/plans", form); toast.success("Plan creado"); setOpen(false); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const delPlan = async (id) => { await api.delete(`/admin/plans/${id}`); load(); };
  const changeSub = async (id, status) => {
    try { await api.patch(`/admin/subscriptions/${id}`, { status }); toast.success("Actualizado"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const activeSubs = subs.filter((s) => s.status === "activa");
  const mrr = activeSubs.filter((s) => s.frequency === "mensual").reduce((a, b) => a + b.price, 0);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>;

  return (
    <div>
      <PageHeader title="Recurrencia" subtitle="Planes de suscripción y clientes recurrentes"
        action={<Button onClick={() => setOpen(true)} className="rounded-lg bg-indigo-600 hover:bg-indigo-700" data-testid="new-plan-btn"><Plus className="h-4 w-4 mr-1" /> Nuevo plan</Button>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <StatCard label="Suscripciones activas" value={activeSubs.length} icon={Repeat} accent="emerald" />
        <StatCard label="Aguardando pago" value={subs.filter((s) => s.status === "aguardando_pago").length} icon={Repeat} accent="amber" />
        <StatCard label="Canceladas" value={subs.filter((s) => s.status === "cancelada").length} icon={Repeat} accent="rose" />
        <StatCard label="MRR estimado" value={formatGs(mrr)} icon={DollarSign} accent="indigo" />
      </div>

      <Tabs defaultValue="subs">
        <TabsList className="mb-4"><TabsTrigger value="subs" data-testid="tab-subs">Suscripciones</TabsTrigger><TabsTrigger value="plans" data-testid="tab-plans">Planes</TabsTrigger></TabsList>

        <TabsContent value="subs">
          {subs.length === 0 ? <EmptyState icon={Repeat} text="Sin suscripciones." />
            : (
              <div className="grid md:grid-cols-2 gap-4">
                {subs.map((s) => (
                  <div key={s.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`sub-${s.id}`}>
                    <div className="flex justify-between items-start">
                      <div><p className="font-semibold text-slate-900">{s.plan_name}</p><p className="text-xs text-slate-400">{s.customer_name} · @{s.instagram_username}</p></div>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${SUB_STATUS[s.status]}`}>{s.status}</span>
                    </div>
                    <div className="mt-2 text-sm text-slate-600">{formatGs(s.price)} / {s.frequency} · Próxima: {formatDate(s.next_renewal)}</div>
                    <Select value={s.status} onValueChange={(v) => changeSub(s.id, v)}>
                      <SelectTrigger className="rounded-lg mt-3 h-9 text-sm" data-testid={`sub-status-${s.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{SUB_STATUSES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            )}
        </TabsContent>

        <TabsContent value="plans">
          {plans.length === 0 ? <EmptyState icon={Repeat} text="Sin planes." />
            : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {plans.map((p) => (
                  <div key={p.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`plan-${p.id}`}>
                    <p className="font-heading font-bold text-slate-900">{p.name}</p>
                    <p className="text-sm text-slate-500">{p.description}</p>
                    <p className="font-heading font-extrabold text-xl text-indigo-600 mt-2">{formatGs(p.price)} <span className="text-sm text-slate-400 font-normal">/ {p.frequency}</span></p>
                    <p className="text-xs text-slate-400 mt-1">{formatNumber(p.quantity)} unidades</p>
                    <Button variant="outline" size="sm" className="rounded-lg mt-3 text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => delPlan(p.id)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            )}
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nuevo plan de suscripción</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg mt-1" placeholder="Plan Instagram Mensual" data-testid="plan-name" /></div>
            <div><Label>Descripción</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-lg mt-1" /></div>
            <div><Label>Servicio</Label>
              <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })}>
                <SelectTrigger className="rounded-lg mt-1" data-testid="plan-service"><SelectValue placeholder="Elegí un servicio" /></SelectTrigger>
                <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Cantidad</Label><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })} className="rounded-lg mt-1" /></div>
              <div><Label>Frecuencia</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                  <SelectTrigger className="rounded-lg mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="mensual">Mensual</SelectItem><SelectItem value="semanal">Semanal</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Precio (₲)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: Number(e.target.value) })} className="rounded-lg mt-1" data-testid="plan-price" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-lg">Cancelar</Button>
            <Button onClick={savePlan} className="rounded-lg bg-indigo-600 hover:bg-indigo-700" data-testid="save-plan">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Subscriptions;
